import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Table, Input, Button, Space, Typography, Alert, Segmented, Tooltip, Tag, Select } from 'antd';
import { ApiOutlined, ReloadOutlined, GlobalOutlined, LockOutlined, AppstoreOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { searchPlugins } from '@/api/plugins';
import { listPluginCategories, indexCategoriesById } from '@/api/pluginCategories';
import type { AiPlugin, PluginCategory } from '@/api/types';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import { CategoryChip } from './CategoryChip';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { colors } from '@/theme/tokens';
import { ToolDetailDrawer } from './ToolDetailDrawer';
import { isGlobalTool, audiencesOf, reachSummary } from './toolMeta';

const { Text } = Typography;

// Platform-level catalog of AI plugins ("Tools" in user-facing language).
// These are the building blocks agents can use to take action.
//
// Two things this page makes visible that the raw list did not:
//  1. GLOBAL reach — a tool with a non-empty UserTypes is force-loaded into
//     every agent of those caller types, ignoring per-agent grants. Admins
//     need to see that at a glance (it's the one kind of tool that can't be
//     trimmed from a single agent). Column + filter below.
//  2. The full description — the table truncates it; click a row for the
//     detail drawer, and the description cell now carries a real hover tooltip.

type ReachFilter = 'all' | 'global' | 'scoped';

export const ToolsListPage = observer(function ToolsListPage() {
  const [plugins, setPlugins] = useState<AiPlugin[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [reach, setReach] = useState<ReachFilter>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AiPlugin | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [categories, setCategories] = useState<PluginCategory[]>([]);
  const [categoryId, setCategoryId] = useState<number | 'all'>('all');

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const result = await searchPlugins({ search, pageSize: 200 });
      setPlugins(result.data ?? []);
      setTotal(result.paging?.totalRecordCount ?? (result.data?.length ?? 0));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tools');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // Categories are a small lookup; load once. A failure here is non-fatal —
    // the catalog still works, rows just render "Uncategorized".
    listPluginCategories().then(setCategories).catch(() => setCategories([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoryById = useMemo(() => indexCategoriesById(categories), [categories]);

  // Both filters are client-side: we already hold the full catalog (≤200 rows),
  // so toggling is instant rather than a round-trip. globalCount drives the
  // segmented badge; per-category counts drive the category dropdown.
  const globalCount = useMemo(() => plugins.filter(isGlobalTool).length, [plugins]);
  const countByCategory = useMemo(() => {
    const m = new Map<number, number>();
    for (const p of plugins) {
      if (p.pluginCategoryId != null) m.set(p.pluginCategoryId, (m.get(p.pluginCategoryId) ?? 0) + 1);
    }
    return m;
  }, [plugins]);
  const shown = useMemo(() => {
    let rows = plugins;
    if (reach === 'global') rows = rows.filter(isGlobalTool);
    else if (reach === 'scoped') rows = rows.filter((p) => !isGlobalTool(p));
    if (categoryId !== 'all') rows = rows.filter((p) => p.pluginCategoryId === categoryId);
    return rows;
  }, [plugins, reach, categoryId]);

  function openDetail(plugin: AiPlugin) {
    setSelected(plugin);
    setDrawerOpen(true);
  }

  // Update the saved row in place so the list reflects an edit without a refetch.
  function applySaved(saved: AiPlugin) {
    setPlugins((prev) => prev.map((p) => (p.id === saved.id ? { ...p, ...saved } : p)));
    setSelected((prev) => (prev && prev.id === saved.id ? { ...prev, ...saved } : prev));
  }

  const columns: ColumnsType<AiPlugin> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
      render: (n: string) => (
        <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13.5, fontWeight: 600 }}>
          {n}
        </Text>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (t?: string) => (
        // Real hover tooltip — the plain ellipsis column swallowed the native
        // title because render returns an element, so the full text was
        // unreadable without opening the row.
        <Tooltip title={t} placement="topLeft" mouseEnterDelay={0.3}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t ?? '—'}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Reach',
      key: 'reach',
      width: 200,
      filters: [
        { text: 'Global', value: 'global' },
        { text: 'Agent-scoped', value: 'scoped' },
      ],
      onFilter: (value, record) => (value === 'global' ? isGlobalTool(record) : !isGlobalTool(record)),
      render: (_t, p) => {
        const global = isGlobalTool(p);
        return (
          <Tooltip title={reachSummary(p)}>
            <Space size={6} align="center">
              {global ? (
                <StatusPill tone="amber">
                  <GlobalOutlined style={{ marginRight: 4 }} />
                  Global
                </StatusPill>
              ) : (
                <StatusPill tone="slate">
                  <LockOutlined style={{ marginRight: 4 }} />
                  Scoped
                </StatusPill>
              )}
              {global &&
                audiencesOf(p).map((a) => (
                  <Tag key={a} color="gold" style={{ fontSize: 10.5, marginInlineEnd: 0 }}>
                    {a}
                  </Tag>
                ))}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Category',
      key: 'category',
      width: 190,
      sorter: (a, b) =>
        (categoryById.get(a.pluginCategoryId ?? -1)?.sortOrder ?? 9999) -
        (categoryById.get(b.pluginCategoryId ?? -1)?.sortOrder ?? 9999),
      render: (_t, p) => <CategoryChip category={p.pluginCategoryId != null ? categoryById.get(p.pluginCategoryId) : null} />,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (v: boolean) =>
        v === false ? <StatusPill tone="slate">Inactive</StatusPill> : <StatusPill tone="green">Active</StatusPill>,
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 72,
      render: (v: number) => (
        <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: colors.slate[500] }}>
          #{v}
        </Text>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="violet"
        icon={<ApiOutlined />}
        title={
          <Space size={6} align="center">
            <span>Tools</span>
            <HelpHotspot topic="tools.plugins" />
          </Space>
        }
        subtitle="Platform-wide catalog of capabilities agents can call. Linked to agents via the Tools tab on each agent."
        count={total}
        countLabel={total === 1 ? 'tool' : 'tools'}
        actions={
          <Space>
            <Input.Search
              placeholder="Search tools…"
              allowClear
              style={{ width: 260 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onSearch={() => void load()}
            />
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => void load()}>
              Refresh
            </Button>
          </Space>
        }
      />

      {error && <Alert type="error" message="Failed to load tools" description={error} showIcon closable />}

      {/* Reach filter — surfaces the count of tools that load into every agent. */}
      <Space size={14} align="center" wrap>
        <Segmented<ReachFilter>
          value={reach}
          onChange={(v) => setReach(v)}
          options={[
            { label: `All (${plugins.length})`, value: 'all' },
            {
              label: (
                <Space size={5}>
                  <GlobalOutlined />
                  {`Global (${globalCount})`}
                </Space>
              ),
              value: 'global',
            },
            {
              label: (
                <Space size={5}>
                  <LockOutlined />
                  {`Agent-scoped (${plugins.length - globalCount})`}
                </Space>
              ),
              value: 'scoped',
            },
          ]}
        />
        <Select<number | 'all'>
          value={categoryId}
          onChange={setCategoryId}
          style={{ minWidth: 240 }}
          suffixIcon={<AppstoreOutlined />}
          options={[
            { label: `All categories (${plugins.length})`, value: 'all' },
            ...categories.map((c) => ({
              label: `${c.name} (${countByCategory.get(c.id) ?? 0})`,
              value: c.id,
            })),
          ]}
        />
        {reach === 'global' && (
          <Text type="secondary" style={{ fontSize: 12.5 }}>
            These tools are loaded into <b>every</b> agent automatically — they can’t be trimmed from a single agent, only un-globaled for everyone.
          </Text>
        )}
      </Space>

      <SectionCard noPadding>
        <Table<AiPlugin>
          rowKey="id"
          columns={columns}
          dataSource={shown}
          loading={loading}
          onRow={(record) => ({
            onClick: () => openDetail(record),
            style: { cursor: 'pointer' },
          })}
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} tools`, style: { padding: '12px 18px', margin: 0 } }}
          locale={{
            emptyText: (
              <BrandedEmpty
                icon={<ApiOutlined />}
                title={reach === 'all' ? 'No tools available' : 'No tools match this filter'}
                description={
                  reach === 'all'
                    ? 'Plugins are registered in code and surfaced here. Empty likely means the API returned no rows.'
                    : 'Try a different reach filter or clear the search.'
                }
              />
            ),
          }}
        />
      </SectionCard>

      <ToolDetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        plugin={selected}
        categories={categories}
        onSaved={applySaved}
      />
    </div>
  );
});
